import type { HTTPClient, APIOptions } from '../core/http-client.js';
import type { 
  GetProfileRequest,
  UserProfile
} from '../types/profile.js';

export class ProfileAPI {
  constructor(private httpClient: HTTPClient) {}

  /**
   * Get user profile information using PSID
   * Requires "Advanced User Profile Access" feature
   */
  async get(request: GetProfileRequest, options?: APIOptions): Promise<UserProfile> {
    const { psid, fields = ['first_name', 'last_name'] } = request;
    
    const queryParams = new URLSearchParams({
      fields: fields.join(','),
    });

    return this.httpClient.request<UserProfile>({
      method: 'GET',
      path: `/${psid}?${queryParams.toString()}`,
      body: undefined,
      accessToken: options?.accessToken,
    });
  }

  /**
   * Get user profile with default fields (first_name, last_name, profile_pic)
   */
  async getBasic(psid: string, options?: APIOptions): Promise<UserProfile> {
    return this.get({
      psid,
      fields: ['first_name', 'last_name', 'profile_pic'],
    }, options);
  }

  /**
   * Get comprehensive user profile with all available fields
   */
  async getFull(psid: string, options?: APIOptions): Promise<UserProfile> {
    return this.get({
      psid,
      fields: ['id', 'name', 'first_name', 'last_name', 'profile_pic', 'locale', 'timezone', 'gender'],
    }, options);
  }

  /**
   * Get user's name (first_name and last_name)
   */
  async getName(psid: string, options?: APIOptions): Promise<UserProfile> {
    return this.get({
      psid,
      fields: ['first_name', 'last_name'],
    }, options);
  }

  /**
   * Get user's profile picture URL
   */
  async getProfilePicture(psid: string, options?: APIOptions): Promise<UserProfile> {
    return this.get({
      psid,
      fields: ['profile_pic'],
    }, options);
  }
}